import {
  ASSET_STATUS_LABELS,
  AssetStatus,
  type AssetLookups,
  type CreateAssetFormValues,
  type OrgUnit,
} from '@gb-ticket/shared-types';
import { useMemo } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { departmentSelectOptions } from '@/platform/features/users/org-select-options';

export const ASSET_NONE = 'none';

type AssetFieldsProps = {
  values: CreateAssetFormValues;
  lookups: AssetLookups | undefined;
  /** Keep an inactive assigned department visible in the select. */
  currentDepartment?: OrgUnit | null;
  disabled?: boolean;
  onChange: (patch: Partial<CreateAssetFormValues>) => void;
};

export function AssetFields({
  values,
  lookups,
  currentDepartment,
  disabled,
  onChange,
}: AssetFieldsProps) {
  const departmentOptions = useMemo(
    () => departmentSelectOptions(lookups?.departments, currentDepartment),
    [lookups?.departments, currentDepartment],
  );
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="asset-name">Bezeichnung</Label>
        <Input
          id="asset-name"
          value={values.name}
          disabled={disabled}
          onChange={(event) => onChange({ name: event.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-type">Typ</Label>
        <Select
          value={values.assetTypeId || undefined}
          onValueChange={(value) => onChange({ assetTypeId: value })}
          disabled={disabled}
        >
          <SelectTrigger id="asset-type" className="w-full">
            <SelectValue placeholder="Typ wählen" />
          </SelectTrigger>
          <SelectContent>
            {(lookups?.types ?? []).map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-status-field">Status</Label>
        <Select
          value={values.status}
          onValueChange={(value) => onChange({ status: value as CreateAssetFormValues['status'] })}
          disabled={disabled}
        >
          <SelectTrigger id="asset-status-field" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(AssetStatus).map((code) => (
              <SelectItem key={code} value={code}>
                {ASSET_STATUS_LABELS[code]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-tag">Inventarnummer</Label>
        <Input
          id="asset-tag"
          value={values.inventoryTag ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ inventoryTag: event.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-serial">Seriennummer</Label>
        <Input
          id="asset-serial"
          value={values.serialNumber ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ serialNumber: event.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-location">Standort</Label>
        <Input
          id="asset-location"
          value={values.location ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ location: event.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-department-field">Abteilung</Label>
        <Select
          value={
            values.departmentId != null && values.departmentId !== ''
              ? values.departmentId
              : ASSET_NONE
          }
          onValueChange={(value) => onChange({ departmentId: value })}
          disabled={disabled}
        >
          <SelectTrigger id="asset-department-field" className="w-full">
            <SelectValue placeholder="Keine Abteilung" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ASSET_NONE}>Keine Abteilung</SelectItem>
            {departmentOptions.map((department) => (
              <SelectItem key={department.id} value={department.id}>
                {department.name}
                {!department.isActive ? ' (archiviert)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {currentDepartment && !currentDepartment.isActive && (
          <p className="text-xs text-muted-foreground">
            Zuordnung archiviert — bitte eine aktive Abteilung wählen.
          </p>
        )}
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="asset-user-field">Zugewiesen an</Label>
        <Select
          value={
            values.assignedUserId != null && values.assignedUserId !== ''
              ? values.assignedUserId
              : ASSET_NONE
          }
          onValueChange={(value) => onChange({ assignedUserId: value })}
          disabled={disabled}
        >
          <SelectTrigger id="asset-user-field" className="w-full">
            <SelectValue placeholder="Kein Nutzer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ASSET_NONE}>Kein Nutzer</SelectItem>
            {(lookups?.users ?? []).map((user) => (
              <SelectItem key={user.id} value={user.id}>
                {user.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-purchase">Kaufdatum</Label>
        <Input
          id="asset-purchase"
          type="date"
          value={values.purchaseDate ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ purchaseDate: event.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-warranty">Garantieende</Label>
        <Input
          id="asset-warranty"
          type="date"
          value={values.warrantyUntil ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ warrantyUntil: event.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="asset-maint">Wartungsende</Label>
        <Input
          id="asset-maint"
          type="date"
          value={values.maintenanceUntil ?? ''}
          disabled={disabled}
          onChange={(event) => onChange({ maintenanceUntil: event.target.value })}
        />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="asset-notes">Notizen</Label>
        <textarea
          id="asset-notes"
          value={values.notes ?? ''}
          disabled={disabled}
          rows={4}
          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
          onChange={(event) => onChange({ notes: event.target.value })}
        />
      </div>
    </div>
  );
}
